import { useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_TIMEOUT_MINUTES = 30;
const WARNING_LEAD_MS = 2 * 60 * 1000;
const PING_INTERVAL_MS = 60 * 1000;
const CHECK_INTERVAL_MS = 5000;
const ACTIVITY_WRITE_THROTTLE_MS = 2000;
const ACTIVITY_KEY = "nova_last_activity";
const LOGOUT_EVENT_KEY = "nova_logout_event";
const NOTICE_KEY = "nova_inactivity_notice";
const NOTICE_MINUTES_KEY = "nova_inactivity_notice_minutes";
const WARNING_TOAST_ID = "nova-inactivity-warning";
// NOTE: these keys only ever hold non-sensitive UI signals (timestamps/flags) used for
// cross-tab sync. The actual JWT/session tokens live in httpOnly cookies, never in
// localStorage/sessionStorage, so they are not readable by client-side script.
// Only real user interaction resets the timer — never background polling/websocket events.
// mousemove + pointermove cover any cursor movement (mouse, trackpad, pen); scroll covers
// scrollbar drags that don't always fire wheel/mousedown on the track itself.
const ACTIVITY_EVENTS = ["mousedown", "mousemove", "pointermove", "keydown", "touchstart", "wheel", "scroll", "click"];

export const InactivityManager = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const loggedOutRef = useRef(false);
  const warningShownRef = useRef(false);
  // Mutable so a config change (admin/org lowers or raises the timeout) reaches an
  // already-signed-in tab the moment /auth/activity echoes the new value back —
  // no page reload or /auth/me re-fetch required.
  const timeoutMsRef = useRef((Number(user?.inactivity_timeout_minutes) || DEFAULT_TIMEOUT_MINUTES) * 60 * 1000);

  useEffect(() => {
    if (user?.inactivity_timeout_minutes) {
      timeoutMsRef.current = Number(user.inactivity_timeout_minutes) * 60 * 1000;
    }
  }, [user?.inactivity_timeout_minutes]);

  const applyEffectiveMinutes = useCallback((minutes) => {
    if (minutes) timeoutMsRef.current = Number(minutes) * 60 * 1000;
  }, []);

  // Applies the redirect + auth-state clear. sessionStorage is the source of truth for the
  // banner (survives ProtectedRoute's own <Navigate> re-render, unlike the URL query string).
  const applyLogout = useCallback((reason) => {
    try {
      if (reason === "inactivity" || reason === "session_expired") sessionStorage.setItem(NOTICE_KEY, reason);
    } catch (err) {
      console.warn("InactivityManager: could not set logout notice", err);
    }
    const showsBanner = reason === "inactivity" || reason === "session_expired";
    navigate(showsBanner ? `/login?reason=${reason}` : "/login", { replace: true });
    setUser(false);
  }, [navigate, setUser]);

  const doLogout = useCallback((reason, minutesUsed) => {
    if (loggedOutRef.current) return;
    loggedOutRef.current = true;
    toast.dismiss(WARNING_TOAST_ID);
    try {
      localStorage.setItem(LOGOUT_EVENT_KEY, JSON.stringify({ reason, ts: Date.now() }));
      if (reason === "inactivity") {
        const minutes = minutesUsed || Math.round(timeoutMsRef.current / 60000);
        sessionStorage.setItem(NOTICE_MINUTES_KEY, String(minutes));
      }
    } catch (err) {
      console.warn("InactivityManager: could not broadcast logout event", err);
    }
    applyLogout(reason);
    axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
  }, [applyLogout]);

  const showExtendedConfirmation = useCallback(() => {
    toast.custom(() => (
      <div data-testid="session-extended-toast" className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-white p-4 shadow-lg dark:border-emerald-500/30 dark:bg-[#111C33]">
        <p className="text-sm font-bold text-emerald-700 dark:text-emerald-400">Session extended</p>
      </div>
    ), { duration: 3000 });
  }, []);

  const extendSession = useCallback(() => {
    axios.post(`${API}/auth/activity`, {}, { withCredentials: true }).then((res) => {
      try {
        localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
      } catch (err) {
        console.warn("InactivityManager: could not record activity", err);
      }
      applyEffectiveMinutes(res?.data?.inactivity_timeout_minutes);
      warningShownRef.current = false;
      toast.dismiss(WARNING_TOAST_ID);
      showExtendedConfirmation();
    }).catch(() => {});
  }, [showExtendedConfirmation, applyEffectiveMinutes]);

  const showWarning = useCallback(() => {
    if (warningShownRef.current) return;
    warningShownRef.current = true;
    toast.custom(() => (
      <div data-testid="inactivity-warning-toast" className="flex items-start gap-3 rounded-xl border border-amber-200 bg-white p-4 shadow-lg dark:border-amber-500/30 dark:bg-[#111C33]">
        <div className="flex-1">
          <p className="text-sm font-bold text-slate-900 dark:text-white">You'll be signed out in 2 minutes</p>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Move your mouse or click below to stay signed in.</p>
        </div>
        <button
          type="button"
          data-testid="stay-signed-in-button"
          onClick={extendSession}
          className="shrink-0 rounded-lg bg-[#0052FF] px-3 py-1.5 text-xs font-bold text-white transition-colors duration-200 hover:bg-blue-700"
        >
          Stay signed in
        </button>
      </div>
    ), { id: WARNING_TOAST_ID, duration: WARNING_LEAD_MS });
  }, [extendSession]);

  const dismissWarning = useCallback(() => {
    if (!warningShownRef.current) return;
    warningShownRef.current = false;
    toast.dismiss(WARNING_TOAST_ID);
  }, []);

  // Cross-tab: react instantly when another Nova Opsis tab logs out.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== LOGOUT_EVENT_KEY || !e.newValue) return;
      let reason = "manual";
      try {
        reason = JSON.parse(e.newValue).reason;
      } catch (err) {
        console.warn("InactivityManager: could not parse logout event", err);
      }
      loggedOutRef.current = true;
      toast.dismiss(WARNING_TOAST_ID);
      applyLogout(reason);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [applyLogout]);

  // Local + cross-tab activity tracking and the sliding inactivity timeout (org/platform
  // configurable). timeoutMsRef is read live on every tick so a config change picked up via
  // a ping/extend response applies immediately, without tearing down this effect.
  useEffect(() => {
    if (!user) return;
    loggedOutRef.current = false;
    warningShownRef.current = false;

    const now = Date.now();
    try {
      localStorage.setItem(ACTIVITY_KEY, String(now));
    } catch (err) {
      console.warn("InactivityManager: could not record activity", err);
    }

    let lastWriteAt = now;
    let dirtySincePing = false;

    const registerActivity = () => {
      const t = Date.now();
      dirtySincePing = true;
      if (t - lastWriteAt > ACTIVITY_WRITE_THROTTLE_MS) {
        lastWriteAt = t;
        try {
          localStorage.setItem(ACTIVITY_KEY, String(t));
        } catch (err) {
          console.warn("InactivityManager: could not record activity", err);
        }
      }
    };
    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, registerActivity, { passive: true }));

    const pingTimer = setInterval(() => {
      if (dirtySincePing) {
        dirtySincePing = false;
        axios.post(`${API}/auth/activity`, {}, { withCredentials: true })
          .then((res) => applyEffectiveMinutes(res?.data?.inactivity_timeout_minutes))
          .catch(() => {});
      }
    }, PING_INTERVAL_MS);

    const checkTimer = setInterval(() => {
      let last = now;
      try {
        last = parseInt(localStorage.getItem(ACTIVITY_KEY), 10) || now;
      } catch (err) {
        console.warn("InactivityManager: could not read activity", err);
      }
      const elapsed = Date.now() - last;
      const timeoutMs = timeoutMsRef.current;
      if (elapsed >= timeoutMs) {
        doLogout("inactivity", Math.round(timeoutMs / 60000));
      } else if (elapsed >= timeoutMs - WARNING_LEAD_MS) {
        showWarning();
      } else if (warningShownRef.current) {
        dismissWarning();
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, registerActivity));
      clearInterval(pingTimer);
      clearInterval(checkTimer);
    };
  }, [user, doLogout, showWarning, dismissWarning, applyEffectiveMinutes]);

  // Backend enforcement fallback: if a real API call fails with 401 while we believed we
  // were authenticated, the session has genuinely expired server-side — sign out. Only
  // attribute it to "inactivity" when the backend detail actually says so; otherwise use a
  // generic reason so we don't show a misleading inactivity-specific banner.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (res) => res,
      (error) => {
        const status = error?.response?.status;
        const url = error?.config?.url || "";
        if (status === 401 && user && !url.includes("/auth/login") && !url.includes("/auth/logout")) {
          const detail = error?.response?.data?.detail;
          const detailText = typeof detail === "string" ? detail.toLowerCase() : "";
          doLogout(detailText.includes("inactiv") ? "inactivity" : "session_expired");
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, [user, doLogout]);

  return null;
};
